/**
 * `responsesFileInput.ts` — turns a picked browser `File` into a **Responses
 * API content part**, the way OpenAI documents file inputs
 * (<https://developers.openai.com/api/docs/guides/file-inputs>).
 *
 * Why this exists: the realtime WebSocket protocol has no file content part —
 * the model only ever receives `input_text` or `input_image` — so the panel's
 * attachment button used to read a `.txt` / `.md` file and paste its contents
 * into the user turn as text. That is the only thing possible on the realtime
 * transport, but it is strictly worse on the Responses transport (the
 * `ResponsesTurnBridge`, active for any role whose configured model is a
 * non-realtime one — today the Workflow Builder): pasted text loses the file's
 * identity, cannot carry a PDF's page images, and cannot carry a spreadsheet,
 * a Word document, or a deck at all.
 *
 * So when the bridge owns the turn, an attachment rides the API's own file
 * input instead:
 *
 * ```json
 * { "type": "input_file",  "file_id": "file-…" }     // documents
 * { "type": "input_image", "file_id": "file-…" }     // images
 * ```
 *
 * **Files are UPLOADED (`POST /v1/files`) and referenced by id, not inlined as
 * base64** — the documented alternative — for a reason specific to this
 * bridge: it runs with `store: false`, so every tool round echoes the whole
 * input array back to the endpoint. A 20 MB PDF inlined as base64 would be
 * re-uploaded on every round of every later turn in the conversation; a
 * `file_id` is ~30 bytes. Inline base64 survives only as the FALLBACK for a
 * small file when the upload leg fails (see {@link K_INLINE_FALLBACK_MAX_BYTES}).
 *
 * Uploaded objects are tracked and deleted on {@link ResponsesFileInputModel.releaseAllAsync},
 * which the orchestrator calls wherever it resets the bridge's conversation
 * (project switch, role switch, `reset_conversation`) — the ids are only
 * meaningful while the history that references them is alive.
 *
 * Transport is the same direct Cognito-authenticated Campfire proxy every
 * other Responses call uses (`aiProxyFetch`), so the provider key stays
 * server-side.
 */

import { aiProxyFetch, ResponsesRequestError } from "@ai/subagents/aiResponsesUtility";

/** Upload/delete timeout. Generous — a 40 MB PDF over a slow link is legitimate. */
const K_FILE_REQUEST_TIMEOUT_MS = 180_000;

/**
 * OpenAI's documented ceiling: under 50 MB per file, and 50 MB across all files
 * in one request. Enforced client-side so an oversized pick fails with a
 * readable reason instead of a 413 from the proxy.
 */
export const K_MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

/**
 * Inline-base64 fallback ceiling, used only when the Files API upload fails.
 * Base64 inflates by 4/3, so 8 MB of bytes is ~10.7 MB on the wire — well
 * inside the 50 MB request cap even after a few tool rounds echo it back.
 */
const K_INLINE_FALLBACK_MAX_BYTES = 8 * 1024 * 1024;

/**
 * Extension → MIME map, transcribed from the guide's supported-format tables.
 * The map is authoritative over `File.type`, which is unreliable in exactly the
 * cases that matter: Windows reports `""` for `.md`, and browsers disagree on
 * the Office types.
 *
 * Text/code extensions collapse to `text/plain` unless the guide names a more
 * specific type, because `text/plain` is unambiguously in the accepted set
 * while a hand-guessed `text/x-…` may not be — and the endpoint keys the
 * extraction on the filename anyway.
 */
const K_PDF_TYPES: Readonly<Record<string, string>> = {
  pdf: "application/pdf",
};

const K_SPREADSHEET_TYPES: Readonly<Record<string, string>> = {
  xls: "application/vnd.ms-excel",
  xla: "application/vnd.ms-excel",
  xlb: "application/vnd.ms-excel",
  xlc: "application/vnd.ms-excel",
  xlm: "application/vnd.ms-excel",
  xlt: "application/vnd.ms-excel",
  xlw: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  tsv: "text/tsv",
  iif: "text/x-iif",
};

const K_DOCUMENT_TYPES: Readonly<Record<string, string>> = {
  doc: "application/msword",
  dot: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  rtf: "application/rtf",
  odt: "application/vnd.oasis.opendocument.text",
};

const K_PRESENTATION_TYPES: Readonly<Record<string, string>> = {
  ppt: "application/vnd.ms-powerpoint",
  pot: "application/vnd.ms-powerpoint",
  ppa: "application/vnd.ms-powerpoint",
  pps: "application/vnd.ms-powerpoint",
  pwz: "application/vnd.ms-powerpoint",
  wiz: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};

const K_TEXT_TYPES: Readonly<Record<string, string>> = {
  // Named explicitly by the guide.
  md: "text/markdown",
  markdown: "text/markdown",
  json: "application/json",
  html: "text/html",
  htm: "text/html",
  xml: "text/xml",
  css: "text/css",
  js: "text/javascript",
  mjs: "text/javascript",
  py: "text/x-python",
  // Everything else the guide lists under Text and Code.
  asm: "text/plain",
  bat: "text/plain",
  c: "text/plain",
  cc: "text/plain",
  conf: "text/plain",
  cpp: "text/plain",
  cxx: "text/plain",
  def: "text/plain",
  dic: "text/plain",
  eml: "message/rfc822",
  h: "text/plain",
  hh: "text/plain",
  ics: "text/calendar",
  ifb: "text/calendar",
  in: "text/plain",
  ksh: "text/plain",
  list: "text/plain",
  log: "text/plain",
  mht: "message/rfc822",
  mhtml: "message/rfc822",
  mime: "message/rfc822",
  nws: "message/rfc822",
  pl: "text/plain",
  rst: "text/plain",
  s: "text/plain",
  sql: "text/plain",
  srt: "text/plain",
  text: "text/plain",
  txt: "text/plain",
  vcf: "text/x-vcard",
  vtt: "text/vtt",
};

/** Vision inputs — these ride `input_image`, not `input_file`. */
const K_IMAGE_TYPES: Readonly<Record<string, string>> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
};

const K_FILE_TYPES: Readonly<Record<string, string>> = {
  ...K_PDF_TYPES,
  ...K_SPREADSHEET_TYPES,
  ...K_DOCUMENT_TYPES,
  ...K_PRESENTATION_TYPES,
  ...K_TEXT_TYPES,
};

/** What kind of content part a picked file becomes. */
export type ResponsesAttachmentKind = "image" | "file";

export interface ResponsesAttachmentFormat {
  readonly kind: ResponsesAttachmentKind;
  /** Extension without the dot, lowercased. `""` when the file has none. */
  readonly extension: string;
  /** The MIME type sent upstream — the table's value, or the browser's as a fallback. */
  readonly mimeType: string;
}

/**
 * One built content part, ready to ride a Responses `input` message.
 * `fileName` is kept beside it for the panel's acknowledgement line.
 */
export interface ResponsesAttachment {
  readonly fileName: string;
  readonly kind: ResponsesAttachmentKind;
  /** `{ type: "input_file" | "input_image", … }`. */
  readonly part: Record<string, unknown>;
  /** Set when the part references an uploaded object (cleared by `releaseAllAsync`). */
  readonly fileId: string | null;
}

/**
 * The file picker's `accept` list for the Responses path: every documented
 * extension, plus the image MIME types so a camera/photos source still offers
 * something on mobile. Extensions are listed as well as MIME types because
 * Windows reports no MIME type at all for several of these.
 */
export const RESPONSES_ATTACHMENT_ACCEPT = [
  ...Object.keys(K_FILE_TYPES).map((ext) => `.${ext}`),
  ...Object.keys(K_IMAGE_TYPES).map((ext) => `.${ext}`),
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "application/pdf",
].join(",");

/** Lowercased extension without the dot, or `""`. */
function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  if (dot < 0 || dot === fileName.length - 1) return "";
  return fileName.slice(dot + 1).toLowerCase();
}

/**
 * Classifies a picked file against the guide's format tables. Returns null when
 * the format is not one the API accepts — the caller must refuse rather than
 * upload, because the endpoint's own rejection arrives as an opaque 400 well
 * after the user has waited for the transfer.
 *
 * The EXTENSION decides; `File.type` is consulted only when the extension is
 * unknown, which is how an `image/png` blob with no name still classifies.
 */
export function classifyResponsesAttachment(file: File): ResponsesAttachmentFormat | null {
  const extension = extensionOf(file.name ?? "");
  const imageType = K_IMAGE_TYPES[extension];
  if (imageType) return { kind: "image", extension, mimeType: imageType };
  const fileType = K_FILE_TYPES[extension];
  if (fileType) return { kind: "file", extension, mimeType: fileType };

  const browserType = (file.type ?? "").toLowerCase();
  if (browserType.startsWith("image/")) {
    const known = Object.values(K_IMAGE_TYPES).includes(browserType);
    if (known) return { kind: "image", extension, mimeType: browserType };
  }
  if (browserType === "application/pdf") {
    return { kind: "file", extension, mimeType: browserType };
  }
  if (browserType.startsWith("text/")) {
    return { kind: "file", extension, mimeType: "text/plain" };
  }
  return null;
}

/** Human-readable size, for refusal messages. */
function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** Chunked so a multi-MB buffer cannot blow the argument limit of `String.fromCharCode`. */
function bytesToBase64(bytes: Uint8Array): string {
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

async function safeText(response: Response): Promise<string> {
  try {
    return (await response.text()).slice(0, 400);
  } catch {
    return "";
  }
}

/**
 * Owns the Responses-side file-input lifecycle for one conversation: format
 * refusal, upload, content-part construction, and cleanup of the objects it
 * uploaded. Owned by `AIOrchestratorModel` alongside `ResponsesTurnBridge`;
 * deliberately not IOC-registered, for the same reason the bridge is not.
 */
export class ResponsesFileInputModel {
  /** Uploaded object ids, in upload order — released together on reset. */
  private readonly m_uploadedFileIds: string[] = [];

  /**
   * Builds the content part for one picked file.
   *
   * @throws {@link ResponsesRequestError} with a user-readable message when the
   * format is unsupported, the file is over the API's size ceiling, or the
   * upload failed and the file is too large for the inline fallback.
   */
  async buildAttachmentAsync(file: File, signal?: AbortSignal): Promise<ResponsesAttachment> {
    const format = classifyResponsesAttachment(file);
    if (!format) {
      throw new ResponsesRequestError(
        `${file.name} is not a file type the model can read.`,
        null,
      );
    }
    if (file.size <= 0) {
      throw new ResponsesRequestError(`${file.name} is empty.`, null);
    }
    if (file.size > K_MAX_ATTACHMENT_BYTES) {
      throw new ResponsesRequestError(
        `${file.name} is ${formatBytes(file.size)} — over the ${formatBytes(K_MAX_ATTACHMENT_BYTES)} limit.`,
        null,
      );
    }

    const fileId = await this.tryUploadAsync(file, format, signal);
    if (fileId) {
      this.m_uploadedFileIds.push(fileId);
      return {
        fileName: file.name,
        kind: format.kind,
        fileId,
        part:
          format.kind === "image"
            ? { type: "input_image", file_id: fileId }
            : { type: "input_file", file_id: fileId },
      };
    }

    // Upload leg unavailable — inline the bytes instead, while they are small
    // enough that echoing them back on every tool round stays affordable.
    if (file.size > K_INLINE_FALLBACK_MAX_BYTES) {
      throw new ResponsesRequestError(
        `Could not upload ${file.name}.`,
        null,
      );
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    const dataUrl = `data:${format.mimeType};base64,${bytesToBase64(bytes)}`;
    return {
      fileName: file.name,
      kind: format.kind,
      fileId: null,
      part:
        format.kind === "image"
          ? { type: "input_image", image_url: dataUrl }
          : { type: "input_file", filename: file.name, file_data: dataUrl },
    };
  }

  /**
   * Deletes every object this conversation uploaded. Best-effort and never
   * throws: a failed delete leaves a stray file on the account, which is
   * strictly better than failing the reset that called it.
   */
  async releaseAllAsync(): Promise<void> {
    const ids = this.m_uploadedFileIds.splice(0, this.m_uploadedFileIds.length);
    await Promise.all(
      ids.map(async (id) => {
        try {
          await aiProxyFetch(
            `/v1/files/${encodeURIComponent(id)}`,
            { method: "DELETE" },
            K_FILE_REQUEST_TIMEOUT_MS,
          );
        } catch (error) {
          console.warn("[ResponsesFileInput] delete failed:", error);
        }
      }),
    );
  }

  /**
   * `POST /v1/files`. Returns the object id, or null when the leg is
   * unavailable so the caller can fall back to inline bytes.
   *
   * Purpose follows the API's own split: `vision` for images, `user_data` for
   * everything the model reads as a document.
   */
  private async tryUploadAsync(
    file: File,
    format: ResponsesAttachmentFormat,
    signal?: AbortSignal,
  ): Promise<string | null> {
    try {
      const form = new FormData();
      form.append("purpose", format.kind === "image" ? "vision" : "user_data");
      // Re-wrap so the MIME type the table resolved is what rides the upload —
      // the browser's own `File.type` is empty for several accepted formats.
      // The boundary is left to the browser; setting Content-Type by hand omits
      // it and the multipart body fails to parse upstream.
      form.append("file", new Blob([file], { type: format.mimeType }), file.name);

      const response = await aiProxyFetch(
        "/v1/files",
        { method: "POST", body: form },
        K_FILE_REQUEST_TIMEOUT_MS,
        signal,
      );
      if (!response.ok) {
        console.warn(
          `[ResponsesFileInput] upload failed: http ${response.status} ${await safeText(response)}`,
        );
        return null;
      }
      const root = (await response.json()) as Record<string, unknown>;
      return typeof root.id === "string" && root.id.length > 0 ? root.id : null;
    } catch (error) {
      // An abort is the caller cancelling — rethrow so it is not mistaken for
      // "the upload leg is unavailable" and silently inlined instead.
      if (signal?.aborted) throw error;
      console.warn("[ResponsesFileInput] upload failed:", error);
      return null;
    }
  }
}
